import axios from "axios";
import { Fragment, useEffect, useState } from "react";
import { useSelector } from "react-redux";

const DEFAULT_META = {
    current_page: 1,
    last_page: 1,
    per_page: 25,
    total: 0,
    from: 0,
    to: 0,
};

function getRowsFromPayload(payload) {
    const collection = payload?.payments || payload?.data || payload;

    if (Array.isArray(collection)) {
        return collection;
    }

    if (Array.isArray(collection?.data)) {
        return collection.data;
    }

    return [];
}

function getMetaFromPayload(payload, fallbackPage, fallbackPerPage) {
    const collection = payload?.payments || payload?.data || payload || {};
    const source = Array.isArray(collection) ? payload || {} : collection;

    return {
        current_page: source.current_page || payload?.current_page || fallbackPage,
        last_page: source.last_page || payload?.last_page || 1,
        per_page: source.per_page || payload?.per_page || fallbackPerPage,
        total: source.total || payload?.total || 0,
        from: source.from || payload?.from || 0,
        to: source.to || payload?.to || 0,
    };
}

function parseOutcomeResponse(value) {
    if (!value) {
        return null;
    }

    if (typeof value === "object") {
        return value;
    }

    if (typeof value !== "string") {
        return null;
    }

    try {
        return JSON.parse(value);
    } catch {
        return null;
    }
}

function getClientName(payment) {
    const name = payment.client_name || payment.customer_name || payment.name;

    if (name) {
        return name;
    }

    const fullName = [payment.first_name, payment.last_name].filter(Boolean).join(" ");
    return fullName || "-";
}

function getClientEmail(payment) {
    return payment.client_email || payment.email || payment.user?.email || "-";
}

function getCustomerCode(payment) {
    return payment.customer_code || payment.customerCode || "-";
}

function getOrderValue(payment) {
    return payment.order_value ?? payment.orderValue ?? payment.amount ?? payment.value;
}

function getOutcomeDescription(payment) {
    const submitState = normalizeState(payment.paym8_submit_state);
    const outcomeState = normalizeState(payment.paym8_outcome_state);

    if (
        submitState === "success" &&
        outcomeState === "incomplete"
    ) {
        return "PayM8 opened, payment not completed";
    }

    const directOutcome =
        payment.paym8_outcome_description ||
        payment.outcome_description ||
        payment.outcomeDescription ||
        payment.failure_reason ||
        payment.failureReason;

    if (directOutcome) {
        return directOutcome;
    }

    const parsedOutcome = parseOutcomeResponse(payment.outcome_response);
    const parsedResult = parseOutcomeResponse(payment.result);

    return (
        parsedOutcome?.data?.outcomeDescription ||
        parsedOutcome?.data?.failureReason ||
        parsedResult?.data?.failureReason ||
        "-"
    );
}

function getStatusMeta(status) {
    const numericStatus = Number(status);

    if (numericStatus === 1) {
        return {
            label: "Success",
            className: "bg-green-100 text-green-800",
        };
    }

    if (numericStatus === 2) {
        return {
            label: "Failed",
            className: "bg-red-100 text-red-800",
        };
    }

    return {
        label: "Pending",
        className: "bg-yellow-100 text-yellow-800",
    };
}

function formatBoolean(value) {
    const booleanValue = normalizeBoolean(value);

    if (booleanValue === true) {
        return "Yes";
    }

    if (booleanValue === false) {
        return "No";
    }

    return "-";
}

function normalizeState(value) {
    return String(value || "").trim().toLowerCase();
}

function normalizeBoolean(value) {
    const normalizedValue = String(value).toLowerCase();

    if (value === true || value === 1 || normalizedValue === "1" || normalizedValue === "true" || normalizedValue === "yes") {
        return true;
    }

    if (value === false || value === 0 || normalizedValue === "0" || normalizedValue === "false" || normalizedValue === "no") {
        return false;
    }

    return null;
}

function formatValue(value) {
    if (value === null || value === undefined || value === "") {
        return "-";
    }

    if (Array.isArray(value)) {
        return value.length ? value.join(", ") : "-";
    }

    if (typeof value === "object") {
        return JSON.stringify(value);
    }

    return String(value);
}

function getStatusLabel(payment) {
    return payment.status_label || getStatusMeta(payment.status).label;
}

function getStatusClass(payment) {
    const label = String(getStatusLabel(payment)).toLowerCase();

    if (label.includes("fail") || label.includes("error")) {
        return "bg-red-100 text-red-800";
    }

    if (label.includes("pending") || label.includes("incomplete")) {
        return "bg-yellow-100 text-yellow-800";
    }

    if (label.includes("success") || label.includes("paid") || label.includes("complete")) {
        return "bg-green-100 text-green-800";
    }

    return getStatusMeta(payment.status).className;
}

function getPaym8SummaryMeta(payment) {
    const submitState = normalizeState(payment.paym8_submit_state);
    const outcomeState = normalizeState(payment.paym8_outcome_state);
    const submitSuccess = normalizeBoolean(payment.paym8_submit_success);

    if (submitState === "success" && outcomeState === "incomplete") {
        return {
            label: "PayM8 opened, payment not completed",
            className: "bg-yellow-100 text-yellow-800",
        };
    }

    if (outcomeState === "success") {
        return {
            label: payment.paym8_outcome_description || "Payment completed",
            className: "bg-green-100 text-green-800",
        };
    }

    if (outcomeState === "failed") {
        return {
            label: payment.paym8_outcome_description || "Payment failed",
            className: "bg-red-100 text-red-800",
        };
    }

    if (submitState === "failed" || submitState === "error" || submitSuccess === false) {
        return {
            label: payment.paym8_submit_failure_reason || "PayM8 start failed",
            className: "bg-red-100 text-red-800",
        };
    }

    if (outcomeState) {
        return {
            label: payment.paym8_outcome_description || outcomeState,
            className: "bg-yellow-100 text-yellow-800",
        };
    }

    if (submitState === "success") {
        return {
            label: "PayM8 opened",
            className: "bg-green-100 text-green-800",
        };
    }

    if (submitState) {
        return {
            label: submitState,
            className: "bg-gray-100 text-gray-800",
        };
    }

    return {
        label: getOutcomeDescription(payment),
        className: "bg-gray-100 text-gray-800",
    };
}

function getPaym8Details(payment) {
    return [
        ["Client Email", getClientEmail(payment)],
        ["Status", payment.status_label],
        ["Submit State", payment.paym8_submit_state],
        ["Submit Success", formatBoolean(payment.paym8_submit_success)],
        ["Submit Failure Reason", payment.paym8_submit_failure_reason],
        ["Outcome State", payment.paym8_outcome_state],
        ["Outcome Code", payment.paym8_outcome_code],
        ["Outcome Description", payment.paym8_outcome_description],
        ["Last Completed Step", payment.paym8_outcome_last_completed_step],
        ["Error Source System", payment.paym8_outcome_error_source_system],
        ["Error Codes", payment.paym8_outcome_error_codes],
    ];
}

function formatMoney(value) {
    if (value === null || value === undefined || value === "") {
        return "-";
    }

    const numericValue = Number(value);

    if (Number.isNaN(numericValue)) {
        return value;
    }

    return `R ${numericValue.toFixed(2)}`;
}

function formatDate(value) {
    if (!value) {
        return "-";
    }

    return String(value).replace("T", " ").slice(0, 19);
}

export function PaymentsReport() {
    const { currentUser } = useSelector((state) => state.auth);
    const token = currentUser?.token;
    const [payments, setPayments] = useState([]);
    const [meta, setMeta] = useState(DEFAULT_META);
    const [customerCode, setCustomerCode] = useState("");
    const [appliedCustomerCode, setAppliedCustomerCode] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [perPage, setPerPage] = useState(25);
    const [loading, setLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [expandedRows, setExpandedRows] = useState({});

    useEffect(() => {
        if (!token) {
            return;
        }

        async function loadPayments() {
            setLoading(true);
            setErrorMessage("");

            try {
                const params = {
                    page: currentPage,
                    per_page: perPage,
                };

                if (appliedCustomerCode.trim()) {
                    params.customer_code = appliedCustomerCode.trim();
                }

                const response = await axios({
                    method: "get",
                    url: `${process.env.REACT_APP_API_URL}admin/payments-report`,
                    headers: { Authorization: `Bearer ${token}` },
                    params,
                });

                setPayments(getRowsFromPayload(response.data));
                setMeta(getMetaFromPayload(response.data, currentPage, perPage));
            } catch (error) {
                setPayments([]);
                setMeta({ ...DEFAULT_META, current_page: currentPage, per_page: perPage });
                setErrorMessage(
                    error?.response?.data?.message ||
                    "Payments report could not be loaded."
                );
            } finally {
                setLoading(false);
            }
        }

        loadPayments();
    }, [token, currentPage, perPage, appliedCustomerCode]);

    function applyFilters(event) {
        event.preventDefault();
        setCurrentPage(1);
        setAppliedCustomerCode(customerCode);
    }

    function clearFilters() {
        setCustomerCode("");
        setAppliedCustomerCode("");
        setCurrentPage(1);
    }

    function goToPreviousPage() {
        setCurrentPage((page) => Math.max(page - 1, 1));
    }

    function goToNextPage() {
        setCurrentPage((page) => Math.min(page + 1, meta.last_page || page + 1));
    }

    function toggleRow(rowKey) {
        setExpandedRows((rows) => ({
            ...rows,
            [rowKey]: !rows[rowKey],
        }));
    }

    return (
        <div className="bg-white">
            <div className="my-8 space-y-6 px-4 sm md:max-w-2xl lg:max-w-4xl xl:max-w-6xl m-auto">
                <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                    <h1>Payments Report</h1>

                    <a
                        className="px-4 py-2 border border-transparent text-sm font-medium border_radius_4 shadow-sm text-white bg-lk_grey_one hover:bg-lk_dark_grey_button_hover uppercase text-center"
                        href="/admin-dashboard"
                    >
                        Back to Admin
                    </a>
                </div>

                <form onSubmit={applyFilters} className="flex flex-col gap-3 border border-gray-200 p-4 sm:flex-row sm:items-end">
                    <div className="flex-1">
                        <label className="block text-sm font-medium text-gray-700" htmlFor="customer_code">
                            Customer Code
                        </label>
                        <input
                            id="customer_code"
                            type="text"
                            value={customerCode}
                            onChange={(event) => setCustomerCode(event.target.value)}
                            placeholder="Example: HS0046"
                            className="mt-1 w-full border border-gray-300 rounded p-2 uppercase"
                        />
                    </div>

                    <div className="w-28">
                        <label className="block text-sm font-medium text-gray-700" htmlFor="per_page">
                            Rows
                        </label>
                        <select
                            id="per_page"
                            value={perPage}
                            onChange={(event) => {
                                setCurrentPage(1);
                                setPerPage(Number(event.target.value));
                            }}
                            className="mt-1 w-full border border-gray-300 rounded py-2 pl-3 pr-10"
                        >
                            <option value={10}>10</option>
                            <option value={25}>25</option>
                            <option value={50}>50</option>
                            <option value={100}>100</option>
                        </select>
                    </div>

                    <div className="flex gap-2">
                        <button
                            type="submit"
                            disabled={loading}
                            className="px-4 py-2 border border-transparent text-sm font-medium border_radius_4 shadow-sm text-white bg-lk_red hover:bg-lk_red_hover uppercase disabled:opacity-50"
                        >
                            Filter
                        </button>
                        <button
                            type="button"
                            disabled={loading}
                            onClick={clearFilters}
                            className="px-4 py-2 border border-gray-300 text-sm font-medium border_radius_4 shadow-sm text-gray-700 bg-white hover:bg-gray-50 uppercase disabled:opacity-50"
                        >
                            Clear
                        </button>
                    </div>
                </form>

                {errorMessage && (
                    <div className="border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                        {errorMessage}
                    </div>
                )}

                <div className="overflow-x-auto border border-gray-200">
                    <table className="w-full table-fixed divide-y divide-gray-200 text-xs">
                        <colgroup>
                            <col className="w-[15%]" />
                            <col className="w-[10%]" />
                            <col className="w-[22%]" />
                            <col className="w-[11%]" />
                            <col className="w-[9%]" />
                            <col className="w-[12%]" />
                            <col className="w-[16%]" />
                            <col className="w-[5%]" />
                        </colgroup>
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-2 py-2 text-left font-medium uppercase text-gray-500">Created</th>
                                <th className="px-2 py-2 text-left font-medium uppercase text-gray-500">Customer Code</th>
                                <th className="px-2 py-2 text-left font-medium uppercase text-gray-500">Client</th>
                                <th className="px-2 py-2 text-left font-medium uppercase text-gray-500">Order</th>
                                <th className="px-2 py-2 text-left font-medium uppercase text-gray-500">Amount</th>
                                <th className="px-2 py-2 text-left font-medium uppercase text-gray-500">Status</th>
                                <th className="px-2 py-2 text-left font-medium uppercase text-gray-500">PayM8 State</th>
                                <th className="px-2 py-2 text-left font-medium uppercase text-gray-500">Details</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 bg-white">
                            {loading && (
                                <tr>
                                    <td colSpan="8" className="px-2 py-8 text-center text-gray-500">
                                        Loading payments...
                                    </td>
                                </tr>
                            )}

                            {!loading && payments.length === 0 && (
                                <tr>
                                    <td colSpan="8" className="px-2 py-8 text-center text-gray-500">
                                        No payment records found.
                                    </td>
                                </tr>
                            )}

                            {!loading && payments.map((payment, index) => {
                                const rowKey = payment.id || payment.token || payment.order_id || index;
                                const paym8Summary = getPaym8SummaryMeta(payment);
                                const paym8Details = getPaym8Details(payment);

                                return (
                                    <Fragment key={rowKey}>
                                        <tr>
                                            <td className="whitespace-nowrap px-2 py-2 text-gray-600">{formatDate(payment.created_at)}</td>
                                            <td className="whitespace-nowrap px-2 py-2 font-medium text-gray-900">{getCustomerCode(payment)}</td>
                                            <td className="whitespace-nowrap px-2 py-2 text-gray-900">
                                                <span className="block truncate" title={getClientName(payment)}>
                                                    {getClientName(payment)}
                                                </span>
                                            </td>
                                            <td className="whitespace-nowrap px-2 py-2 text-gray-600">{payment.order_id || "-"}</td>
                                            <td className="whitespace-nowrap px-2 py-2 text-gray-900">{formatMoney(getOrderValue(payment))}</td>
                                            <td className="whitespace-nowrap px-2 py-2">
                                                <span className={`inline-flex px-2 py-1 text-xs font-semibold ${getStatusClass(payment)}`}>
                                                    {getStatusLabel(payment)}
                                                </span>
                                            </td>
                                            <td className="px-2 py-2">
                                                <span className={`block truncate px-2 py-1 text-xs font-semibold ${paym8Summary.className}`} title={paym8Summary.label}>
                                                    {paym8Summary.label}
                                                </span>
                                            </td>
                                            <td className="whitespace-nowrap px-2 py-2">
                                                <button
                                                    type="button"
                                                    onClick={() => toggleRow(rowKey)}
                                                    className="text-xs font-medium text-lk_red hover:text-lk_red_hover"
                                                >
                                                    {expandedRows[rowKey] ? "Hide" : "View"}
                                                </button>
                                            </td>
                                        </tr>

                                        {expandedRows[rowKey] && (
                                            <tr key={`${rowKey}-details`} className="bg-gray-50">
                                                <td colSpan="8" className="px-2 py-4">
                                                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                                                        {paym8Details.map(([label, value]) => (
                                                            <div key={label}>
                                                                <div className="text-xs font-medium uppercase text-gray-500">{label}</div>
                                                                <div className="mt-1 break-words text-xs text-gray-900">{formatValue(value)}</div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </Fragment>
                                );
                            })}
                        </tbody>
                    </table>
                </div>

                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <p className="text-sm text-gray-600">
                        Showing {meta.from || 0} to {meta.to || payments.length} of {meta.total || payments.length} payments
                    </p>

                    <div className="flex items-center gap-2">
                        <button
                            type="button"
                            disabled={loading || Number(meta.current_page) <= 1}
                            onClick={goToPreviousPage}
                            className="px-4 py-2 border border-gray-300 text-sm font-medium border_radius_4 shadow-sm text-gray-700 bg-white hover:bg-gray-50 uppercase disabled:opacity-50"
                        >
                            Previous
                        </button>
                        <span className="text-sm text-gray-600">
                            Page {meta.current_page || currentPage} of {meta.last_page || 1}
                        </span>
                        <button
                            type="button"
                            disabled={loading || Number(meta.current_page) >= Number(meta.last_page || 1)}
                            onClick={goToNextPage}
                            className="px-4 py-2 border border-gray-300 text-sm font-medium border_radius_4 shadow-sm text-gray-700 bg-white hover:bg-gray-50 uppercase disabled:opacity-50"
                        >
                            Next
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
